import { useState, useEffect, useRef, useCallback } from "react";
import { X, Loader2, Copy, ExternalLink, Play, Square, RotateCcw, Check, ChevronLeft, Download } from 'lucide-react';
import authService from '../services/AuthService';
import { MARKETPLACE_LOGOS, API_BASE } from '../consts';

// ── localStorage helpers (small, bounded data only: filters / id input) ──
const SK_SCANNED = (mp, source) => `sfs_scanned_${mp}_${source}`;
const SK_SELLERS = (mp) => `sfs_found_sellers_${mp}`;
const SK_FILTERS = (mp) => `sfs_filters_${mp}`;
const SK_SOURCE = (mp) => `sfs_source_${mp}`;
const SK_IDINPUT = (mp) => `sfs_idinput_${mp}`;

function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }
function loadObj(key) { try { return JSON.parse(localStorage.getItem(key) || "{}"); } catch { return {}; } }
function saveObj(key, o) { try { localStorage.setItem(key, JSON.stringify(o)); } catch { } }
function parseNum(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return v;
    const cleaned = String(v).replace(/[,+]/g, '').trim();
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}

// ── IndexedDB helpers (used for the two stores that grow unbounded during a long
// scan: scanned-ID sets and found-seller maps). IndexedDB is async — it never
// blocks the UI thread the way a giant synchronous JSON.stringify into
// localStorage does — and its quota is far larger, so an overnight scan can't
// blow past a ~5-10MB ceiling anymore. ──
const IDB_NAME = 'sfs_storefront_sourcing';
const IDB_STORE = 'kv';

function idbOpen() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, 1);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(IDB_STORE)) {
                req.result.createObjectStore(IDB_STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(key) {
    try {
        const db = await idbOpen();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(IDB_STORE, 'readonly');
            const req = tx.objectStore(IDB_STORE).get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    } catch (e) { console.error('idbGet error', key, e); return undefined; }
}

async function idbSet(key, value) {
    try {
        const db = await idbOpen();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(IDB_STORE, 'readwrite');
            tx.objectStore(IDB_STORE).put(value, key);
            tx.oncomplete = () => resolve(true);
            tx.onerror = () => reject(tx.error);
        });
    } catch (e) { console.error('idbSet error', key, e); return false; }
}

// One-time migration: if this key still has (possibly huge/corrupt) data sitting
// in localStorage from before this fix, pull it into IndexedDB and delete the
// localStorage copy — this both preserves old progress and frees up the quota
// that was likely why localStorage writes started failing overnight.
async function migrateLegacyIfNeeded(key) {
    try {
        const raw = localStorage.getItem(key);
        if (raw == null) return;
        const existing = await idbGet(key);
        if (existing === undefined) {
            await idbSet(key, JSON.parse(raw));
        }
        localStorage.removeItem(key);
    } catch {
        // Legacy value was likely truncated/corrupted by the earlier quota issue —
        // nothing usable to migrate, just clear it out.
        try { localStorage.removeItem(key); } catch { }
    }
}

async function idbLoadSet(key) {
    await migrateLegacyIfNeeded(key);
    const arr = await idbGet(key);
    return new Set(Array.isArray(arr) ? arr : []);
}
async function idbSaveSet(key, s) { await idbSet(key, [...s]); }
async function idbLoadObj(key) {
    await migrateLegacyIfNeeded(key);
    const obj = await idbGet(key);
    return obj && typeof obj === 'object' ? obj : {};
}
async function idbSaveObj(key, o) { await idbSet(key, o); }

const DEFAULT_AMAZON_FILTERS = {
    fulfillment: 'all',       // all | fba | fbm
    minStock: '',
    minReviews: '',
    maxReviews: '',
    minRating: '',
    minCatalogSize: '',
    maxCatalogSize: '',
};

const DEFAULT_WALMART_FILTERS = {
    fulfillment: 'all',       // all | wfs | sf
    proBadgeOnly: false,
    minStock: '', maxStock: '',
    minRating: '', maxRating: '',
    minTotalRatings: '', maxTotalRatings: '',
    minReviewsWithText: '', maxReviewsWithText: '',
    minItemCount: '', maxItemCount: '',
};

const RESULTS_PAGE_SIZE = 100;

const lbl = { fontSize: 11, fontWeight: 600, color: '#374151' };
const inp = { width: '100%', marginTop: 4, padding: '6px 8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 };

export default function StorefrontSourcingModal({ onClose }) {
    const [marketplace, setMarketplace] = useState(null);       // null | 'amazon' | 'walmart'
    const [sourceMethod, setSourceMethod] = useState(null);      // amazon: 'historical' | 'list' — walmart: 'list'
    const [idInput, setIdInput] = useState('');
    const [historicalCount, setHistoricalCount] = useState(null);
    const [loadingHistoricalCount, setLoadingHistoricalCount] = useState(false);

    const [amazonFilters, setAmazonFilters] = useState(() => ({ ...DEFAULT_AMAZON_FILTERS, ...loadObj(SK_FILTERS('amazon')) }));
    const [walmartFilters, setWalmartFilters] = useState(() => ({ ...DEFAULT_WALMART_FILTERS, ...loadObj(SK_FILTERS('walmart')) }));
    const [showFilters, setShowFilters] = useState(true);

    const [started, setStarted] = useState(false);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState({ scanned: 0, total: 0, currentId: '' });
    const [foundSellers, setFoundSellers] = useState({});
    const [copied, setCopied] = useState(false);
    const [loadingIds, setLoadingIds] = useState(false);
    const [resultsPage, setResultsPage] = useState(1); // paginate the on-screen list only; copy/CSV still use the full set

    const scannedIds = useRef(new Set());
    const stopFlag = useRef(false);
    const idQueue = useRef([]);
    const sellerInfoCache = useRef({}); // walmart seller_id -> seller-info payload

    // Load per-marketplace saved progress/source/input when a marketplace is picked
    // Fires when the marketplace changes: loads the found-sellers map from IndexedDB
    // (unique, shared across source methods) and the saved ID input text.
    useEffect(() => {
        if (!marketplace) return;
        let cancelled = false;
        idbLoadObj(SK_SELLERS(marketplace)).then(obj => { if (!cancelled) setFoundSellers(obj); });
        // sourceMethod is intentionally NOT auto-restored — always let the user
        // re-pick it so switching between Historical Leads <-> List stays possible.
        try {
            const savedInput = localStorage.getItem(SK_IDINPUT(marketplace));
            if (savedInput) setIdInput(savedInput);
        } catch { }
        return () => { cancelled = true; };
    }, [marketplace]);

    // Fires when the source method changes: each (marketplace, source) pair gets
    // its own scanned-set and progress counter, so switching from "Historical
    // Leads" to "ASIN List" (or back) never carries over the other's scan count.
    useEffect(() => {
        if (!marketplace || !sourceMethod) return;
        let cancelled = false;
        setResultsPage(1);
        (async () => {
            const s = await idbLoadSet(SK_SCANNED(marketplace, sourceMethod));
            if (cancelled) return;
            scannedIds.current = s;
            setProgress({ scanned: s.size, total: 0, currentId: '' });
            setStarted(s.size > 0);
            idQueue.current = [];
        })();
        return () => { cancelled = true; };
    }, [marketplace, sourceMethod]);

    useEffect(() => {
        if (marketplace) { try { localStorage.setItem(SK_IDINPUT(marketplace), idInput); } catch { } }
    }, [marketplace, idInput]);

    useEffect(() => {
        if (marketplace === 'amazon' && historicalCount === null) {
            setLoadingHistoricalCount(true);
            authService.authenticatedFetch(`${API_BASE}/api/historical-leads/asins`)
                .then(r => r.json())
                .then(d => setHistoricalCount((d.asins || []).length))
                .catch(() => setHistoricalCount(0))
                .finally(() => setLoadingHistoricalCount(false));
        }
    }, [marketplace]);

    const updateAmazonFilter = (key, val) => {
        setAmazonFilters(prev => { const next = { ...prev, [key]: val }; saveObj(SK_FILTERS('amazon'), next); return next; });
    };
    const updateWalmartFilter = (key, val) => {
        setWalmartFilters(prev => { const next = { ...prev, [key]: val }; saveObj(SK_FILTERS('walmart'), next); return next; });
    };

    // ================= AMAZON =================
    const passesAmazonFilters = (offer, sellerData) => {
        if (offer.isAmazon) return false; // always excluded
        if (amazonFilters.fulfillment === 'fba' && !offer.isFBA) return false;
        if (amazonFilters.fulfillment === 'fbm' && offer.isFBA) return false;
        if (amazonFilters.minStock !== '' && offer.stock !== -1 && offer.stock < Number(amazonFilters.minStock)) return false;
        if (!sellerData) return false;
        if (amazonFilters.minReviews !== '' && (sellerData.seller_reviews ?? 0) < Number(amazonFilters.minReviews)) return false;
        if (amazonFilters.maxReviews !== '' && (sellerData.seller_reviews ?? 0) > Number(amazonFilters.maxReviews)) return false;
        if (amazonFilters.minRating !== '' && (sellerData.seller_rating ?? 0) < Number(amazonFilters.minRating)) return false;
        if (amazonFilters.minCatalogSize !== '' && (sellerData.seller_asins ?? 0) < Number(amazonFilters.minCatalogSize)) return false;
        if (amazonFilters.maxCatalogSize !== '' && (sellerData.seller_asins ?? 0) > Number(amazonFilters.maxCatalogSize)) return false;
        return true;
    };

    const scanOneAsin = async (asin) => {
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/api/amazon/advanced/${asin}`);
            if (!res.ok) return;
            const data = await res.json();
            const offers = data.offers_table || [];
            const sellersData = data.sellers_data || {};
            const updates = {};
            for (const offer of offers) {
                const sellerData = sellersData[offer.sellerId];
                if (!passesAmazonFilters(offer, sellerData)) continue;
                updates[offer.sellerId] = {
                    seller_name: sellerData?.seller_name || '',
                    seller_asins: sellerData?.seller_asins ?? null,
                    seller_reviews: sellerData?.seller_reviews ?? null,
                    seller_rating: sellerData?.seller_rating ?? null,
                    isFBA: offer.isFBA,
                    matchedId: asin,
                    matchedPrice: offer.price,
                    foundAt: Date.now(),
                };
            }
            if (Object.keys(updates).length > 0) {
                setFoundSellers(prev => {
                    const next = { ...prev, ...updates };
                    idbSaveObj(SK_SELLERS('amazon'), next); // fire-and-forget async write, no localStorage quota risk
                    return next;
                });
            }
        } catch (e) { console.error('scanOneAsin error', e); }
    };

    // ================= WALMART =================
    const passesWalmartOfferFilters = (seller) => {
        if (seller.is_walmart) return false; // always excluded
        if (walmartFilters.fulfillment === 'wfs' && seller.wfs !== 'WFS') return false;
        if (walmartFilters.fulfillment === 'sf' && seller.wfs === 'WFS') return false;
        if (walmartFilters.proBadgeOnly && !seller.has_seller_badge) return false;
        const stock = parseNum(seller.stock);
        if (walmartFilters.minStock !== '' && stock !== null && stock < Number(walmartFilters.minStock)) return false;
        if (walmartFilters.maxStock !== '' && stock !== null && stock > Number(walmartFilters.maxStock)) return false;
        return true;
    };

    const passesWalmartSellerInfoFilters = (info) => {
        if (!info) return false;
        const rating = parseNum(info.rawRating);
        const totalReviews = parseNum(info.totalReviews);
        const reviewsWithText = parseNum(info.reviewsWithText);
        const itemCount = parseNum(info.itemCount);

        if (walmartFilters.minRating !== '' && rating !== null && rating < Number(walmartFilters.minRating)) return false;
        if (walmartFilters.maxRating !== '' && rating !== null && rating > Number(walmartFilters.maxRating)) return false;
        if (walmartFilters.minTotalRatings !== '' && totalReviews !== null && totalReviews < Number(walmartFilters.minTotalRatings)) return false;
        if (walmartFilters.maxTotalRatings !== '' && totalReviews !== null && totalReviews > Number(walmartFilters.maxTotalRatings)) return false;
        if (walmartFilters.minReviewsWithText !== '' && reviewsWithText !== null && reviewsWithText < Number(walmartFilters.minReviewsWithText)) return false;
        if (walmartFilters.maxReviewsWithText !== '' && reviewsWithText !== null && reviewsWithText > Number(walmartFilters.maxReviewsWithText)) return false;
        if (walmartFilters.minItemCount !== '' && itemCount !== null && itemCount < Number(walmartFilters.minItemCount)) return false;
        if (walmartFilters.maxItemCount !== '' && itemCount !== null && itemCount > Number(walmartFilters.maxItemCount)) return false;
        return true;
    };

    const getWalmartSellerInfo = async (sellerId) => {
        if (sellerInfoCache.current[sellerId]) return sellerInfoCache.current[sellerId];
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/api/walmart/seller-info`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ seller_id: sellerId }),
            });
            const data = await res.json();
            sellerInfoCache.current[sellerId] = data;
            await sleep(800); // pace per-seller lookups
            return data;
        } catch (e) {
            console.error('seller-info error', e);
            return null;
        }
    };

    const scanOneItemId = async (itemId) => {
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/api/walmart/sellers/${itemId}`);
            await new Promise(resolve => setTimeout(resolve, 5000));
            if (!res.ok) return;
            const data = await res.json();
            const sellers = data.sellers || [];
            const updates = {};

            for (const seller of sellers) {
                if (!passesWalmartOfferFilters(seller)) continue;
                const info = await getWalmartSellerInfo(seller.seller_id);
                if (!passesWalmartSellerInfoFilters(info)) continue;

                updates[seller.seller_id] = {
                    seller_name: info?.sellerName || seller.seller_name || '',
                    seller_rating: info?.rawRating ?? null,
                    seller_total_ratings: info?.totalReviews ?? null,
                    seller_reviews_with_text: info?.reviewsWithText ?? null,
                    seller_item_count: (info?.itemCount === 'N/A' ? null : info?.itemCount) ?? null,
                    wfs: seller.wfs,
                    has_seller_badge: seller.has_seller_badge,
                    matchedId: itemId,
                    matchedPrice: seller.price_value,
                    foundAt: Date.now(),
                };
            }

            if (Object.keys(updates).length > 0) {
                setFoundSellers(prev => {
                    const next = { ...prev, ...updates };
                    idbSaveObj(SK_SELLERS('walmart'), next); // fire-and-forget async write, no localStorage quota risk
                    return next;
                });
            }
        } catch (e) { console.error('scanOneItemId error', e); }
    };

    // ================= SHARED SCAN LOOP =================
    const fetchIdsFromInput = () => idInput.split('\n').map(l => l.trim()).filter(Boolean);

    const fetchHistoricalAsins = async () => {
        setLoadingIds(true);
        try {
            const res = await authService.authenticatedFetch(`${API_BASE}/api/historical-leads/asins`);
            const data = await res.json();
            return data.asins || [];
        } catch { return []; }
        finally { setLoadingIds(false); }
    };

    const runScan = useCallback(async () => {
        stopFlag.current = false;
        setRunning(true);
        setStarted(true);
        setShowFilters(false);

        if (idQueue.current.length === 0) {
            let allIds = [];
            if (marketplace === 'amazon' && sourceMethod === 'historical') {
                allIds = await fetchHistoricalAsins();
            } else {
                allIds = fetchIdsFromInput();
            }
            idQueue.current = allIds.filter(a => !scannedIds.current.has(a));
            setProgress(p => ({ ...p, total: allIds.length }));
        }

        while (idQueue.current.length > 0 && !stopFlag.current) {
            const id = idQueue.current.shift();
            setProgress(p => ({ ...p, currentId: id }));

            if (marketplace === 'amazon') await scanOneAsin(id);
            else await scanOneItemId(id);

            scannedIds.current.add(id);
            await idbSaveSet(SK_SCANNED(marketplace, sourceMethod), scannedIds.current);
            setProgress(p => ({ ...p, scanned: scannedIds.current.size, currentId: id }));

            await sleep(3500); // adjust to match the real cost/rate-limit of these endpoints
        }

        setRunning(false);
    }, [marketplace, sourceMethod, idInput, amazonFilters, walmartFilters]);

    const stopScan = () => { stopFlag.current = true; setRunning(false); };

    const resetScan = () => {
        if (!window.confirm('Reset scanned IDs and found sellers? This cannot be undone.')) return;
        stopFlag.current = true;
        scannedIds.current = new Set();
        idQueue.current = [];
        idbSaveSet(SK_SCANNED(marketplace, sourceMethod), scannedIds.current);
        setFoundSellers({});
        idbSaveObj(SK_SELLERS(marketplace), {});
        setProgress({ scanned: 0, total: 0, currentId: '' });
        setStarted(false);
        setRunning(false);
        setResultsPage(1);
    };

    const backOneStep = () => {
        stopFlag.current = true;
        setRunning(false);
        if (sourceMethod) {
            setSourceMethod(null);
            setIdInput('');
        } else if (marketplace) {
            setMarketplace(null);
        }
    };

    const sellerIdList = Object.keys(foundSellers);
    const visibleSellerIds = sellerIdList.slice(0, resultsPage * RESULTS_PAGE_SIZE);
    const copySellerIds = () => {
        if (sellerIdList.length === 0) return;
        navigator.clipboard.writeText(sellerIdList.join('\n'));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const downloadCSV = () => {
        if (sellerIdList.length === 0) return;
        const isAmazon = marketplace === 'amazon';
        const headers = isAmazon
            ? ['Seller ID', 'Seller Name', 'Catalog Size', 'Reviews', 'Rating %', 'Fulfillment', 'Matched ASIN', 'Matched Price']
            : ['Seller ID', 'Seller Name', 'Item Count', 'Rating', 'Total Ratings', 'Reviews w/ Text', 'Fulfillment', 'Matched Item ID', 'Matched Price'];

        const esc = (v) => {
            if (v === null || v === undefined) return '';
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };

        const rows = sellerIdList.map(sid => {
            const s = foundSellers[sid];
            return isAmazon
                ? [sid, s.seller_name, s.seller_asins, s.seller_reviews, s.seller_rating, s.isFBA ? 'FBA' : 'FBM', s.matchedId, s.matchedPrice]
                : [sid, s.seller_name, s.seller_item_count, s.seller_rating, s.seller_total_ratings, s.seller_reviews_with_text, s.wfs, s.matchedId, s.matchedPrice];
        });

        const csv = [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${marketplace}_sellers_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const idLabel = marketplace === 'amazon' ? 'ASIN' : 'Item ID';

    return (
        <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 680, height: '88vh', maxHeight: 720, boxShadow: '0 32px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: '1px solid #f3f4f6', background: 'linear-gradient(135deg,#7c3aed,#db2777)' }}>
                    {marketplace && (
                        <button onClick={backOneStep} style={{ border: 'none', background: 'rgba(255,255,255,0.2)', width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer' }}>
                            <ChevronLeft size={16} />
                        </button>
                    )}
                    <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: 800, fontSize: 16, color: 'white' }}>✨ Storefront Sourcing</p>
                        <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>Find sellers worth stalking based on your criteria</p>
                    </div>
                    <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><X size={16} /></button>
                </div>

                <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, padding: '14px 20px' }}>

                    {/* STEP 1: marketplace */}
                    {!marketplace && (
                        <div>
                            <p style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>Which marketplace?</p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {['amazon', 'walmart'].map(mp => (
                                    <button key={mp} onClick={() => setMarketplace(mp)}
                                        style={{ padding: 20, border: '2px solid #e5e7eb', borderRadius: 12, background: 'white', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                        <img src={MARKETPLACE_LOGOS[mp]} alt={mp} style={{ width: 48, height: 48, objectFit: 'contain' }} />
                                        <span style={{ fontWeight: 700, textTransform: 'capitalize' }}>{mp}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* STEP 2: source method */}
                    {marketplace && !sourceMethod && (
                        <div>
                            <p style={{ fontWeight: 700, fontSize: 14, color: '#374151', marginBottom: 12 }}>Where should we source IDs from?</p>

                            {marketplace === 'amazon' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <button onClick={() => setSourceMethod('historical')} disabled={loadingHistoricalCount || !historicalCount}
                                        style={{ textAlign: 'left', padding: 14, borderRadius: 12, border: '2px solid #e5e7eb', background: 'white', cursor: (loadingHistoricalCount || !historicalCount) ? 'not-allowed' : 'pointer', opacity: (loadingHistoricalCount || !historicalCount) ? 0.5 : 1 }}>
                                        <div style={{ fontWeight: 700, fontSize: 13 }}>Use My Historical Leads Found On Ungate Crawler</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                                            {loadingHistoricalCount ? 'Loading...' : `${(historicalCount || 0).toLocaleString()} ASINs available`}
                                        </div>
                                    </button>
                                    <button onClick={() => setSourceMethod('list')}
                                        style={{ textAlign: 'left', padding: 14, borderRadius: 12, border: '2px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>
                                        <div style={{ fontWeight: 700, fontSize: 13 }}>Provide ASIN List</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Paste your own ASINs, one per line</div>
                                    </button>
                                    <span
                                        style={{ fontSize: 12, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                                        Hint: Export deals found from other scans or use Keepa Product Finder, and use the ASINs to source storefronts.
                                    </span>
                                </div>
                            )}

                            {marketplace === 'walmart' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <button onClick={() => setSourceMethod('list')}
                                        style={{ textAlign: 'left', padding: 14, borderRadius: 12, border: '2px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>
                                        <div style={{ fontWeight: 700, fontSize: 13 }}>Provide Item ID List</div>
                                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Paste Walmart Item IDs, one per line</div>
                                    </button>
                                    <span
                                        style={{ fontSize: 12, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                                        Hint: Export deals found from other scans, and use their item IDs to source storefronts.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3: id input (list method only) */}
                    {marketplace && sourceMethod === 'list' && (
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                                {idLabel} List <span style={{ fontWeight: 400, color: '#9ca3af' }}>(one per line)</span>
                            </label>
                            <textarea value={idInput} disabled={running} onChange={e => setIdInput(e.target.value)} rows={6}
                                placeholder={marketplace === 'amazon' ? 'B0XXXXXXXX\nB0YYYYYYYY' : '123456789\n987654321'}
                                style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: 12 }} />
                            {started && (
                                <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                    Resuming? Re-paste the same list — already-scanned IDs are skipped automatically.
                                </p>
                            )}
                        </div>
                    )}

                    {/* FILTERS */}
                    {marketplace && sourceMethod && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 16 }}>
                            <button onClick={() => setShowFilters(o => !o)}
                                style={{ width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', border: 'none', borderRadius: showFilters ? '12px 12px 0 0' : 12, cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#374151' }}>
                                <span>🎯 Filters</span>
                                <span style={{ fontSize: 11, color: '#9ca3af' }}>{showFilters ? '▲ Hide' : '▼ Show'}</span>
                            </button>

                            {showFilters && marketplace === 'amazon' && (
                                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={lbl}>Fulfillment</label>
                                        <select disabled={running} value={amazonFilters.fulfillment} onChange={e => updateAmazonFilter('fulfillment', e.target.value)} style={inp}>
                                            <option value="all">All (FBA + FBM)</option>
                                            <option value="fba">FBA only</option>
                                            <option value="fbm">FBM only</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={lbl}>Min stock</label>
                                        <input type="number" min={0} disabled={running} value={amazonFilters.minStock} onChange={e => updateAmazonFilter('minStock', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min seller reviews</label>
                                        <input type="number" min={0} disabled={running} value={amazonFilters.minReviews} onChange={e => updateAmazonFilter('minReviews', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max seller reviews</label>
                                        <input type="number" min={0} disabled={running} value={amazonFilters.maxReviews} onChange={e => updateAmazonFilter('maxReviews', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min seller rating %</label>
                                        <input type="number" min={0} max={100} disabled={running} value={amazonFilters.minRating} onChange={e => updateAmazonFilter('minRating', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min catalog size (ASINs)</label>
                                        <input type="number" min={0} disabled={running} value={amazonFilters.minCatalogSize} onChange={e => updateAmazonFilter('minCatalogSize', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max catalog size (ASINs)</label>
                                        <input type="number" min={0} disabled={running} value={amazonFilters.maxCatalogSize} onChange={e => updateAmazonFilter('maxCatalogSize', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                </div>
                            )}

                            {showFilters && marketplace === 'walmart' && (
                                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label style={lbl}>Fulfillment</label>
                                        <select disabled={running} value={walmartFilters.fulfillment} onChange={e => updateWalmartFilter('fulfillment', e.target.value)} style={inp}>
                                            <option value="all">All (WFS + SF)</option>
                                            <option value="wfs">WFS only</option>
                                            <option value="sf">Seller Fulfilled only</option>
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                                            <input type="checkbox" disabled={running} checked={walmartFilters.proBadgeOnly} onChange={e => updateWalmartFilter('proBadgeOnly', e.target.checked)} className="accent-purple-600" />
                                            Pro Seller Badge only
                                        </label>
                                    </div>
                                    <div>
                                        <label style={lbl}>Min stock</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.minStock} onChange={e => updateWalmartFilter('minStock', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max stock</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.maxStock} onChange={e => updateWalmartFilter('maxStock', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min rating (out of 5)</label>
                                        <input type="number" min={0} max={5} step={0.1} disabled={running} value={walmartFilters.minRating} onChange={e => updateWalmartFilter('minRating', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max rating (out of 5)</label>
                                        <input type="number" min={0} max={5} step={0.1} disabled={running} value={walmartFilters.maxRating} onChange={e => updateWalmartFilter('maxRating', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min total ratings</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.minTotalRatings} onChange={e => updateWalmartFilter('minTotalRatings', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max total ratings</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.maxTotalRatings} onChange={e => updateWalmartFilter('maxTotalRatings', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min reviews with text</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.minReviewsWithText} onChange={e => updateWalmartFilter('minReviewsWithText', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max reviews with text</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.maxReviewsWithText} onChange={e => updateWalmartFilter('maxReviewsWithText', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Min item count (catalog size)</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.minItemCount} onChange={e => updateWalmartFilter('minItemCount', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Max item count (catalog size)</label>
                                        <input type="number" min={0} disabled={running} value={walmartFilters.maxItemCount} onChange={e => updateWalmartFilter('maxItemCount', e.target.value)} placeholder="—" style={inp} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* PROGRESS */}
                    {marketplace && sourceMethod && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                            <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: '12px 14px' }}>
                                <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#7c3aed' }}>{idLabel}s scanned</p>
                                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#581c87' }}>
                                    {progress.scanned.toLocaleString()}{progress.total > 0 ? ` / ${progress.total.toLocaleString()}` : ''}
                                </p>
                                {progress.currentId && running && (
                                    <p style={{ margin: '4px 0 0', fontSize: 10, color: '#9ca3af' }}>Scanning: {progress.currentId}</p>
                                )}
                            </div>
                            <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 12, padding: '12px 14px' }}>
                                <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: '#db2777' }}>Sellers found</p>
                                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#831843' }}>{sellerIdList.length.toLocaleString()}</p>
                            </div>
                        </div>
                    )}

                    {/* RESULTS (paginated — only renders resultsPage * RESULTS_PAGE_SIZE rows at a time
                        so a scan that has found thousands of sellers can't choke the DOM) */}
                    {marketplace && sourceMethod && sellerIdList.length > 0 && (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 8, maxHeight: 220, overflowY: 'auto', marginTop: 10 }}>
                            {visibleSellerIds.map(sid => {
                                const s = foundSellers[sid];
                                return (
                                    <div key={sid} style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11 }}>
                                        <div>
                                            <span style={{ fontWeight: 700, color: '#374151' }}>{s.seller_name || sid}</span>{' '}
                                            <span style={{ color: '#9ca3af', fontFamily: 'monospace' }}>{sid}</span>
                                        </div>
                                        <div style={{ color: '#9ca3af', display: 'flex', gap: 8 }}>
                                            {marketplace === 'amazon' && s.seller_asins != null && <span>{s.seller_asins.toLocaleString()} asins</span>}
                                            {marketplace === 'amazon' && s.seller_reviews != null && <span>{s.seller_reviews.toLocaleString()} reviews</span>}
                                            {marketplace === 'walmart' && s.seller_item_count != null && <span>{s.seller_item_count.toLocaleString()} items</span>}
                                            {marketplace === 'walmart' && s.seller_rating != null && <span>{s.seller_rating}/5 ★</span>}
                                            {marketplace === 'walmart' && s.seller_total_ratings != null && <span>{s.seller_total_ratings.toLocaleString()} ratings</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {marketplace && sourceMethod && sellerIdList.length > visibleSellerIds.length && (
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <button onClick={() => setResultsPage(p => p + 1)}
                                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white', color: '#374151', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                Load more ({sellerIdList.length - visibleSellerIds.length} remaining)
                            </button>
                        </div>
                    )}

                    {marketplace && sourceMethod && sellerIdList.length > 0 && (
                        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, padding: 14, marginBottom: 4 }}>
                            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#065f46', fontWeight: 600 }}>
                                Next step: copy the seller IDs, then paste them into Storefront Stalking to scan their catalogs.
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button onClick={downloadCSV} title="Download CSV" style={{ padding: '9px 12px', borderRadius: 9, border: '1px solid #e5e7eb', background: 'white', color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Download size={13} />
                                </button>
                                <button onClick={copySellerIds} style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: 'none', background: '#059669', color: 'white', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    {copied ? <Check size={13} /> : <Copy size={13} />}
                                    {copied ? 'Copied!' : `Copy ${sellerIdList.length} Seller IDs`}
                                </button>
                                <a href="https://www.multisellerpro.com/storefront-scan" target="_blank" rel="noopener noreferrer"
                                    style={{ flex: 1, padding: '9px 0', borderRadius: 9, textDecoration: 'none', background: '#111827', color: 'white', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                    <ExternalLink size={13} /> Open Storefront Stalking
                                </a>
                            </div>
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                {marketplace && sourceMethod && (
                    <div style={{ flexShrink: 0, display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #f3f4f6' }}>
                        {!running ? (
                            <button onClick={runScan} disabled={loadingIds || (sourceMethod === 'list' && idInput.trim().length === 0)}
                                style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: 'none', background: 'linear-gradient(90deg,#7c3aed,#db2777)', color: 'white', fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: (loadingIds || (sourceMethod === 'list' && idInput.trim().length === 0)) ? 0.5 : 1 }}>
                                <Play size={14} />
                                {loadingIds ? `Loading ${idLabel}s…` : started ? 'Resume Scan' : 'Start Scan'}
                            </button>
                        ) : (
                            <button onClick={stopScan} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: 'none', background: '#ef4444', color: 'white', fontWeight: 800, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                                <Square size={14} /> Stop
                            </button>
                        )}
                        <button onClick={resetScan} style={{ padding: '11px 16px', borderRadius: 10, border: '1px solid #e5e7eb', background: 'white', color: '#6b7280', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <RotateCcw size={13} /> Reset
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}